import { isTokenExpired } from './token';

type ApiError = { response?: { status?: number; data?: { message?: unknown } } } | null | undefined;

/**
 * Decides whether an API error means the admin's session is really over.
 *
 * A 401 used to sign the user out unconditionally. But one endpoint rejecting
 * a token that is still valid (e.g. a Lambda whose JWT verification is
 * misconfigured in one environment) is not a dead session — signing out there
 * turned every visit to App Users / Permissions into a logout. So a 401 only
 * ends the session when the stored token is missing or past its `exp`, or the
 * backend explicitly says the token expired.
 */
export function shouldEndSession(error: ApiError, token: string | null): boolean {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.message ?? '').toLowerCase();
  const saysExpired = message.includes('token expired');
  const saysInvalid = message.includes('invalid token');

  if (status !== 401 && !saysExpired && !saysInvalid) return false;
  if (saysExpired) return true;
  return !token || isTokenExpired(token);
}

/** True for a 401 that did not end the session (see `shouldEndSession`). */
export function isRejectedButSignedIn(error: ApiError, token: string | null): boolean {
  return error?.response?.status === 401 && !shouldEndSession(error, token);
}

export const REJECTED_BUT_SIGNED_IN_MESSAGE =
  'The server could not authorize this request. You are still signed in — if it keeps happening, contact support.';
